/**
 * Internationalization (i18n) Configuration
 *
 * This module sets up i18n with locale-aware routing:
 * - Default locale (en): No URL prefix (e.g., /)
 * - Other locales (vi, ja): URL prefix (e.g., /vi, /ja)
 *
 * Router links will automatically work without lang param because:
 * 1. Routes use optional lang param: /:lang(vi|ja)?
 * 2. All route names are shared across locales
 * 3. Vue Router will match routes with or without lang param
 *
 * If you need explicit locale control in templates, use withLang():
 * Example: :to="withLang({ name: 'FeatureIndex' })"
 */
import { createI18n } from 'vue-i18n'
import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router'
import en from './en/common.json'
import vi from './vi/common.json'
import ja from './ja/common.json'

// Get saved language from localStorage, browser, or default to 'en'
const getSavedLanguage = (): string => {
  if (typeof window !== 'undefined') {
    // First check localStorage
    const saved = localStorage.getItem('preferred_language')
    if (saved && ['en', 'vi', 'ja'].includes(saved)) {
      return saved
    }

    // Then check browser language
    const browserLang = navigator.language.toLowerCase()
    if (browserLang.startsWith('vi')) {
      return 'vi'
    } else if (browserLang.startsWith('ja')) {
      return 'ja'
    }
  }
  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: getSavedLanguage(), // Load from localStorage or default
  fallbackLocale: 'en',
  messages: {
    en,
    vi,
    ja,
  },
})

// Save language preference to localStorage
export function saveLanguagePreference(lang: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('preferred_language', lang)
  }
}

// Get current locale from route or i18n
export function getCurrentLocale() {
  return i18n.global.locale.value
}

// Simple helper to add lang param to route if needed (for use in templates)
// Usage in template: :to="withLang({ name: 'FeatureIndex' })"
export function withLang(to: RouteLocationRaw, lang?: string | null): RouteLocationRaw {
  const currentLang = lang !== undefined ? lang : i18n.global.locale.value

  // If it's a string path
  if (typeof to === 'string') {
    return currentLang && currentLang !== 'en' ? `/${currentLang}${to}` : to
  }

  // If it's a route object with name
  if (typeof to === 'object' && to !== null && 'name' in to) {
    // For non-English, add lang to params
    if (currentLang && currentLang !== 'en') {
      return {
        ...to,
        params: {
          ...(('params' in to && to.params) || {}),
          lang: currentLang,
        },
      }
    }
    // For English (default), return without lang param
    return to
  }

  return to
}

// Composable to handle locale-aware routing
export function useLocaleRouter() {
  const router = useRouter()
  const route = useRoute()

  // Get current lang from route params, default to null for 'en'
  const currentLang = () => {
    const lang = route.params.lang as string
    return lang === 'vi' || lang === 'ja' ? lang : null
  }

  // Generate route location with proper lang param
  const localeRoute = (to: RouteLocationRaw): RouteLocationRaw => {
    const lang = currentLang()

    // If it's a string path, return as-is
    if (typeof to === 'string') {
      return lang ? `/${lang}${to}` : to
    }

    // If it's a route object with name
    if (typeof to === 'object' && to !== null && 'name' in to) {
      // For non-English, add lang to params
      if (lang) {
        return {
          ...to,
          params: {
            ...(('params' in to && to.params) || {}),
            lang,
          },
        }
      }
      // For English (default), return without lang param
      return to
    }

    return to
  }

  return {
    router,
    route,
    currentLang,
    localeRoute,
  }
}
